import { isSessionStorageAllowed } from '../web-storage';

const LANDING_ATTRIBUTION_KEY = 'chownow:diner:landing-attribution';

export type LandingAttribution = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  external_referrer: string | null;
};

const EMPTY_LANDING_ATTRIBUTION: LandingAttribution = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  external_referrer: null,
};

/**
 * origin + pathname of `document.referrer` when it comes from another host,
 * otherwise null
 */
const getExternalReferrer = (): string | null => {
  if (!document.referrer) return null;

  try {
    const referrer = new URL(document.referrer);

    if (referrer.host === window.location.host) return null;

    return `${referrer.origin}${referrer.pathname}`;
  } catch {
    return null;
  }
};

/**
 * records the utm params and external referrer of the page load into session storage.
 * a load with neither keeps the previously captured values, so in-session
 * reloads and internal navigations don't erase the landing attribution
 */
export const captureLandingAttribution = (): void => {
  if (!isSessionStorageAllowed()) {
    return;
  }

  const queryParams = new URLSearchParams(window.location.search);
  const attribution: LandingAttribution = {
    utm_source: queryParams.get('utm_source'),
    utm_medium: queryParams.get('utm_medium'),
    utm_campaign: queryParams.get('utm_campaign'),
    external_referrer: getExternalReferrer(),
  };

  if (Object.values(attribution).every((value) => value === null)) {
    return;
  }

  sessionStorage.setItem(LANDING_ATTRIBUTION_KEY, JSON.stringify(attribution));
};

export const getLandingAttribution = (): LandingAttribution => {
  if (!isSessionStorageAllowed()) {
    return EMPTY_LANDING_ATTRIBUTION;
  }

  try {
    const stored = JSON.parse(
      sessionStorage.getItem(LANDING_ATTRIBUTION_KEY) ?? 'null'
    ) as Partial<LandingAttribution> | null;

    return { ...EMPTY_LANDING_ATTRIBUTION, ...stored };
  } catch {
    return EMPTY_LANDING_ATTRIBUTION;
  }
};
